import axios from 'axios';

import config from '@src/config';
import { mergeAuthState } from '@src/utils/authState';
import { formUtils, httpUtils } from '@src/utils';

function normalizeBrowserDevHost(value) {
  if (typeof window === 'undefined' || !value) {
    return value;
  }

  const browserHostname = window.location.hostname;
  if (browserHostname !== 'localhost') {
    return value;
  }

  if (value === '0.0.0.0' || value === '0.0.0.0:8000') {
    return value.replace('0.0.0.0', 'localhost');
  }

  try {
    const url = new URL(value);
    if (url.hostname === '0.0.0.0') {
      url.hostname = 'localhost';
      return url.toString().replace(/\/$/, '');
    }
  } catch (error) {
    // Ignore non-URL values and fall through unchanged.
  }

  return value;
}

function DefaultClientConfig() {
  this.API_VERSION = 'api';

  const detectedProtocol = typeof window !== 'undefined'
    ? window.location.protocol.replace(':', '')
    : 'http';
  const rawProto = import.meta.env.SSL;
  this.PROTO = rawProto === 'on' ? 'https' : rawProto === 'off' ? 'http' : detectedProtocol || 'http';

  const detectedDomain = typeof window !== 'undefined' ? window.location.host : 'localhost:8000';
  this.DOMAIN = normalizeBrowserDevHost(import.meta.env.DOMAIN || detectedDomain);

  const explicitBase = normalizeBrowserDevHost(import.meta.env.API_BASE_URL);
  this.BASE_URL = explicitBase || `${this.PROTO}://${this.DOMAIN}`;
  this.API_URL = `${this.BASE_URL}/${this.API_VERSION}`;
}

const defaultClientConfig = new DefaultClientConfig();

function getCredentials() {
  return localStorage.getItem('auth')
    ? JSON.parse(localStorage.getItem('auth'))
    : {} || {};
}

function setCredentials(credentials) {
  return localStorage.setItem('auth', JSON.stringify(credentials));
}

function removeCredentials() {
  return localStorage.removeItem('auth');
}

function redirectToLoginPage() {
  window.location.replace(config.LOGIN_URL);
}

function createDefaultAxiosClient() {
  const instance = axios.create({ baseURL: defaultClientConfig.API_URL });

  // Config axios options here
  instance.defaults.withCredentials = true;
  instance.defaults.headers.common['Content-Type'] = formUtils.CONTENT_TYPES.JSON;
  return instance;
}

const defaultAxiosClient = createDefaultAxiosClient();

function createAuthAxiosClient() {
  const instance = axios.create({ baseURL: defaultClientConfig.API_URL });

  // Config axios options here
  instance.defaults.withCredentials = true;
  instance.defaults.headers.common['Content-Type'] = formUtils.CONTENT_TYPES.JSON;
  instance.interceptors.response.use(undefined, (error) => {
    if (httpUtils.isUnauthorizedStatus(error.response)) {
      const { refresh_token = null } = getCredentials();

      if (!refresh_token) {
        removeCredentials();
        redirectToLoginPage();
        return Promise.reject(new Error('RefeshToken parse error.'));
      }

      return defaultAxiosClient
        .post('accounts/auth/token/refresh/', { refresh_token })
        .then((response) => {
          setCredentials(mergeAuthState(getCredentials(), response.data));
          // eslint-disable-next-line no-param-reassign
          error.config.headers.Authorization = `Bearer ${response.data.access_token}`;
          return instance.request(error.config);
        })
        .catch(() => {
          removeCredentials();
          redirectToLoginPage();
          return Promise.reject(new Error('RefeshToken ontain error.'));
        });
    }
    return Promise.reject(error);
  });
  return instance;
}

const authAxiosClient = createAuthAxiosClient();

function request({
  auth = true,
  url = '',
  method = 'GET',
  payload = null,
  multipart = false,
  headers = {},
  options = {},
  serializer = formUtils.serialize,
}) {
  let jsonOrFormData = payload;
  let instance = null;
  const dataOrParams = ['GET', 'DELETE'].includes(method) ? 'params' : 'data';

  if (auth) {
    // Set axiosInstanceWithAuth as base
    instance = authAxiosClient;
    // Get access token from auth state
    const { access_token = null } = getCredentials();
    // Access token not founded
    if (!access_token) {
      removeCredentials();
      redirectToLoginPage();
      return Promise.reject(new Error('AccessToken parse error.'));
    }

    // Access token founded
    instance.defaults.headers.common.Authorization = `Bearer ${access_token}`;
  } else {
    // Set axiosInstanceWithoutAuth as base
    instance = defaultAxiosClient;
  }

  // Set Content-Type per-request (never mutate shared instance defaults)
  if (multipart) {
    if (!(payload instanceof FormData)) {
      jsonOrFormData = serializer(payload);
    }
    headers['Content-Type'] = formUtils.CONTENT_TYPES.MULTIPART;
  } else {
    headers['Content-Type'] = formUtils.CONTENT_TYPES.JSON;
  }

  return new Promise((resolve, reject) => {
    instance
      .request({
        url,
        method,
        headers,
        [dataOrParams]: jsonOrFormData,
        ...options,
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        reject(error);
      });
  });
}

function bulkDeactivateClientZips(ids = [], applyToFiltered = false) {
  return request({
    url: 'dashboard/client-zips/bulk-deactivate/',
    method: 'POST',
    payload: { ids, apply_to_filtered: applyToFiltered },
  });
}

const defaultClient = { request, bulkDeactivateClientZips };

export default defaultClient;
export { authAxiosClient, defaultAxiosClient, bulkDeactivateClientZips };
